'use client'

import { useState } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { Cpu, ShieldCheck, Eye, Mic, Speaker, Database, BrainCircuit, HardDrive, CheckCircle2, Sparkles, Monitor, Box } from 'lucide-react'

type ProductVersion = 'standard' | 'hologram'

export default function HardwarePage() {
    const [email, setEmail] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isSuccess, setIsSuccess] = useState(false)
    const [selectedVersion, setSelectedVersion] = useState<ProductVersion>('standard')

    const handleScrollToWaitlist = () => {
        const element = document.getElementById('waitlist-form')
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' })
        }
    }

    const handleWaitlistSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!email) return

        setIsSubmitting(true)

        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 1500))

        setIsSubmitting(false)
        setIsSuccess(true)
        setEmail('')
    }

    const products = {
        standard: {
            name: 'LinkBox AI Server',
            tagline: 'The Foundation of Local Intelligence',
            description: 'Your always-on personal AI Agent Hub. A local-first RAG, voice, and agent orchestration system powered by high-performance desktop AI hardware.',
            price: 'Starting at $799',
            image: '/images/linkbox_holotube_m5.png',
            imageAlt: 'LinkBox AI HoloTube agent display',
            badge: 'CORE',
            badgeColor: 'from-green-400 to-emerald-600',
            features: [
                'High-performance desktop AI compute platform',
                '10-core CPU + up to 10-core GPU',
                '16-core Neural Engine + GPU Neural Accelerators',
                '153GB/s unified memory bandwidth',
                'NVMe SSD local RAG storage',
                'Thunderbolt + Ethernet connectivity',
                'Voice Agent + HoloTube companion support',
                'Privacy-first — local data and local inference options',
                'Desktop Orchestrator support',
            ]
        },
        hologram: {
            name: 'LinkBox AI Holo',
            tagline: 'Meet Your Agent. Face to Face.',
            description: 'Everything in the standard server, plus a stunning holographic display cylinder that brings your agent to life. See your agent, talk to it, and watch it work in full 3D.',
            price: 'Starting at $1,399',
            image: '/images/linkbox_hologram_device.png',
            imageAlt: 'LinkBox AI Holo with Holographic Display',
            badge: 'HOLO',
            badgeColor: 'from-cyan-400 to-blue-600',
            features: [
                'Everything in LinkBox AI Server, plus:',
                'Holographic agent avatar display cylinder',
                'Real-time animated 3D assistant',
                'Visual feedback for tasks & status',
                'Ambient glow & mood lighting',
                'Customizable hologram personas',
                'Voice-synced lip animation',
                'Premium matte black enclosure',
            ]
        }
    }

    const activeProduct = products[selectedVersion]

    return (
        <div className="min-h-screen bg-[#0B0F19] text-white">
            {/* Navigation Bar */}
            <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0B0F19]/80 backdrop-blur-xl border-b border-white/10">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <Link href="/" className="flex items-center gap-3">
                            <Image
                                src="/linkbox-logo.png"
                                alt="LinkBox AI"
                                width={40}
                                height={40}
                                className="rounded-xl"
                                unoptimized
                            />
                            <span className="text-xl font-bold">
                                <span className="text-[#4ade80]">Link</span>
                                <span className="text-white">Box AI</span>
                            </span>
                        </Link>

                        <div className="hidden md:flex items-center gap-8">
                            <Link href="/features" className="text-gray-400 hover:text-white transition-colors text-sm font-medium">Features</Link>
                            <Link href="/ecosystem" className="text-gray-400 hover:text-white transition-colors text-sm font-medium">Ecosystem</Link>
                            <Link href="/messaging-integration.html" className="text-gray-400 hover:text-white transition-colors text-sm font-medium">Messaging</Link>
                            <Link href="/hardware" className="text-white transition-colors text-sm font-medium">Hardware</Link>
                            <Link href="/#pricing" className="text-gray-400 hover:text-white transition-colors text-sm font-medium">Pricing</Link>
                            <Link href="/hipaa" className="text-gray-400 hover:text-white transition-colors text-sm font-medium">HIPAA</Link>
                            <Link href="/robotics" className="text-gray-400 hover:text-white transition-colors text-sm font-medium">Robotics</Link>
                        </div>

                        <div className="flex items-center gap-3">
                            <Link
                                href="/login"
                                className="text-gray-300 hover:text-white transition-colors text-sm font-medium px-4 py-2"
                            >
                                Log In
                            </Link>
                            <Link
                                href="/signup"
                                className="bg-gradient-to-r from-[#4ade80] to-[#22c55e] text-gray-900 px-5 py-2 rounded-lg text-sm font-semibold hover:from-[#86efac] hover:to-[#4ade80] transition-all shadow-lg shadow-green-500/25"
                            >
                                Get Started Free
                            </Link>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-16">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-green-500/10 via-[#0B0F19] to-[#0B0F19]" />

                <div className="container mx-auto px-4 relative z-10 py-20">
                    {/* Badge */}
                    <div className="text-center mb-12">
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-green-500/10 border border-green-500/20 text-green-400 text-sm font-medium tracking-wide mb-6">
                            <span className="relative flex h-2 w-2">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                            </span>
                            COMING LATE 2026
                        </div>

                        <h1 className="text-5xl lg:text-7xl font-bold tracking-tight mb-6">
                            The Ultimate <br />
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-600">
                                Home Agentic Hub
                            </span>
                        </h1>

                        <p className="text-xl text-gray-400 max-w-2xl mx-auto leading-relaxed mb-10">
                            Two powerful configurations. One mission: your personal, private AI agent that lives in your home.
                        </p>
                    </div>

                    {/* Product Version Selector */}
                    <div className="flex justify-center mb-16">
                        <div className="inline-flex bg-white/5 border border-white/10 rounded-2xl p-1.5 gap-2">
                            <button
                                onClick={() => setSelectedVersion('standard')}
                                className={`flex items-center gap-2.5 px-6 py-3.5 rounded-xl text-sm font-semibold transition-all duration-300 ${selectedVersion === 'standard'
                                    ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-black shadow-lg shadow-green-500/25'
                                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                                    }`}
                            >
                                <Box className="w-4 h-4" />
                                LinkBox AI Server
                            </button>
                            <button
                                onClick={() => setSelectedVersion('hologram')}
                                className={`flex items-center gap-2.5 px-6 py-3.5 rounded-xl text-sm font-semibold transition-all duration-300 ${selectedVersion === 'hologram'
                                    ? 'bg-gradient-to-r from-cyan-400 to-blue-600 text-black shadow-lg shadow-cyan-500/25'
                                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                                    }`}
                            >
                                <Sparkles className="w-4 h-4" />
                                LinkBox AI Holo
                            </button>
                        </div>
                    </div>

                    {/* Product Display */}
                    <div className="grid lg:grid-cols-2 gap-16 items-center max-w-6xl mx-auto">
                        {/* Product Info */}
                        <div className="space-y-8" key={selectedVersion}>
                            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold tracking-widest ${selectedVersion === 'standard'
                                ? 'bg-green-500/10 border border-green-500/20 text-green-400'
                                : 'bg-cyan-500/10 border border-cyan-500/20 text-cyan-400'
                                }`}>
                                {activeProduct.badge}
                            </div>

                            <h2 className="text-4xl lg:text-5xl font-bold">
                                {activeProduct.name}
                            </h2>

                            <p className={`text-2xl font-semibold text-transparent bg-clip-text bg-gradient-to-r ${activeProduct.badgeColor}`}>
                                {activeProduct.tagline}
                            </p>

                            <p className="text-lg text-gray-400 leading-relaxed">
                                {activeProduct.description}
                            </p>

                            {/* Features List */}
                            <ul className="space-y-3">
                                {activeProduct.features.map((feature, i) => (
                                    <li key={i} className="flex items-start gap-3 text-gray-300">
                                        <CheckCircle2 className={`w-5 h-5 mt-0.5 flex-shrink-0 ${selectedVersion === 'standard' ? 'text-green-400' : 'text-cyan-400'
                                            }`} />
                                        <span>{feature}</span>
                                    </li>
                                ))}
                            </ul>

                            <div className="flex flex-col sm:flex-row gap-4 pt-4">
                                <div className="text-3xl font-bold text-white">
                                    {activeProduct.price}
                                </div>
                                <button
                                    onClick={handleScrollToWaitlist}
                                    className={`px-8 py-4 font-bold rounded-xl transition-all ${selectedVersion === 'standard'
                                        ? 'bg-green-500 hover:bg-green-600 text-black shadow-[0_0_20px_rgba(34,197,94,0.3)] hover:shadow-[0_0_30px_rgba(34,197,94,0.5)]'
                                        : 'bg-gradient-to-r from-cyan-400 to-blue-500 hover:from-cyan-300 hover:to-blue-400 text-black shadow-[0_0_20px_rgba(34,211,238,0.3)] hover:shadow-[0_0_30px_rgba(34,211,238,0.5)]'
                                        }`}
                                >
                                    Reserve Now
                                </button>
                            </div>
                        </div>

                        {/* Product Image */}
                        <div className="relative group perspective-1000" key={`img-${selectedVersion}`}>
                            <div className={`absolute -inset-1 rounded-[2.5rem] blur-2xl opacity-20 group-hover:opacity-40 transition duration-1000 ${selectedVersion === 'standard'
                                ? 'bg-gradient-to-r from-green-500 to-emerald-600'
                                : 'bg-gradient-to-r from-cyan-400 to-blue-600'
                                }`}></div>
                            <div className="relative rounded-[2rem] overflow-hidden border border-white/10 bg-black/50 backdrop-blur-xl shadow-2xl transition-transform duration-500 group-hover:scale-[1.02]">
                                <Image
                                    src={activeProduct.image}
                                    alt={activeProduct.imageAlt}
                                    width={800}
                                    height={800}
                                    className="w-full h-auto object-cover"
                                    priority
                                />

                                {/* Floating Spec Badge */}
                                <div className="absolute bottom-6 left-6 right-6 p-4 bg-black/80 backdrop-blur-md rounded-xl border border-white/10 flex justify-between items-center text-sm text-gray-300">
                                    <span className="flex items-center gap-2"><Cpu className="w-4 h-4 text-green-400" /> Desktop AI</span>
                                    <span className="w-px h-4 bg-white/20"></span>
                                    <span className="flex items-center gap-2"><BrainCircuit className="w-4 h-4 text-green-400" /> Neural Engine</span>
                                    <span className="w-px h-4 bg-white/20"></span>
                                    {selectedVersion === 'hologram' ? (
                                        <span className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-cyan-400" /> Hologram</span>
                                    ) : (
                                        <span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-green-400" /> Local Privacy</span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Side-by-Side Comparison */}
            <section className="py-24 bg-[#0B0F19] relative border-t border-white/5">
                <div className="container mx-auto px-4 relative z-10">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-5xl font-bold mb-6">Choose Your Configuration</h2>
                        <p className="text-gray-400 max-w-2xl mx-auto text-lg">
                            Both models share the same powerful agent core. The Holo edition adds a jaw-dropping holographic interface.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
                        {/* Standard Card */}
                        <div className={`relative p-8 rounded-3xl border transition-all cursor-pointer ${selectedVersion === 'standard'
                            ? 'bg-green-500/5 border-green-500/30 shadow-xl shadow-green-500/5'
                            : 'bg-white/5 border-white/10 hover:border-white/20'
                            }`}
                            onClick={() => setSelectedVersion('standard')}>
                            <div className="aspect-square relative rounded-2xl overflow-hidden mb-6 bg-black/50">
                                <Image
                                    src="/images/linkbox_device_cameras.png"
                                    alt="LinkBox AI Server"
                                    fill
                                    className="object-cover"
                                />
                            </div>
                            <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-green-500/10 border border-green-500/20 text-green-400 text-xs font-bold tracking-widest mb-4">
                                CORE
                            </div>
                            <h3 className="text-2xl font-bold mb-2">LinkBox AI Server</h3>
                            <p className="text-gray-400 mb-6 leading-relaxed">
                                The powerhouse. Vision, voice, local RAG — everything you need for a private agent that runs 24/7 on your home network.
                            </p>
                            <div className="flex items-center justify-between">
                                <span className="text-2xl font-bold text-white">$799</span>
                                <button
                                    onClick={(e) => { e.stopPropagation(); handleScrollToWaitlist() }}
                                    className="px-5 py-2.5 bg-green-500/20 border border-green-500/30 text-green-400 font-semibold rounded-xl hover:bg-green-500/30 transition-all text-sm"
                                >
                                    Reserve
                                </button>
                            </div>
                        </div>

                        {/* Hologram Card */}
                        <div className={`relative p-8 rounded-3xl border transition-all cursor-pointer ${selectedVersion === 'hologram'
                            ? 'bg-cyan-500/5 border-cyan-500/30 shadow-xl shadow-cyan-500/5'
                            : 'bg-white/5 border-white/10 hover:border-white/20'
                            }`}
                            onClick={() => setSelectedVersion('hologram')}>
                            {/* Popular badge */}
                            <div className="absolute -top-3 right-6 px-3 py-1 bg-gradient-to-r from-cyan-400 to-blue-500 text-black text-xs font-bold rounded-full">
                                ✨ PREMIUM
                            </div>
                            <div className="aspect-square relative rounded-2xl overflow-hidden mb-6 bg-black/50">
                                <Image
                                    src="/images/linkbox_hologram_device.png"
                                    alt="LinkBox AI Holo"
                                    fill
                                    className="object-cover"
                                />
                            </div>
                            <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-xs font-bold tracking-widest mb-4">
                                HOLO
                            </div>
                            <h3 className="text-2xl font-bold mb-2">LinkBox AI Holo</h3>
                            <p className="text-gray-400 mb-6 leading-relaxed">
                                Everything in the Server, plus a holographic display that brings your agent to life. See your assistant in full 3D.
                            </p>
                            <div className="flex items-center justify-between">
                                <span className="text-2xl font-bold text-white">$1,399</span>
                                <button
                                    onClick={(e) => { e.stopPropagation(); handleScrollToWaitlist() }}
                                    className="px-5 py-2.5 bg-cyan-500/20 border border-cyan-500/30 text-cyan-400 font-semibold rounded-xl hover:bg-cyan-500/30 transition-all text-sm"
                                >
                                    Reserve
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* RAG & Features Grid */}
            <section className="py-24 bg-[#0B0F19] relative">
                <div className="absolute inset-0 bg-[url('/images/grid-pattern.svg')] opacity-5" />
                <div className="container mx-auto px-4 relative z-10">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-5xl font-bold mb-6">Complete Sensory Intelligence</h2>
                        <p className="text-gray-400 max-w-2xl mx-auto text-lg">
                            Your personal agent doesn't just wait for prompts. It sees, hears, speaks, and remembers everything locally on your network.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {/* Feature 1: Vision */}
                        <div className="p-8 rounded-3xl bg-white/5 border border-white/10 hover:border-green-500/50 transition-all group">
                            <div className="w-14 h-14 rounded-2xl bg-green-500/20 flex items-center justify-center mb-6 text-green-400 group-hover:scale-110 transition-transform">
                                <Eye className="w-8 h-8" />
                            </div>
                            <h3 className="text-2xl font-bold mb-3">AI Vision Security</h3>
                            <p className="text-gray-400 leading-relaxed">
                                Dual high-res cameras with real-time face recognition. It knows who it's communicating with and monitors 360° of your space for unauthorized access.
                            </p>
                        </div>

                        {/* Feature 2: Voice */}
                        <div className="p-8 rounded-3xl bg-white/5 border border-white/10 hover:border-green-500/50 transition-all group">
                            <div className="w-14 h-14 rounded-2xl bg-blue-500/20 flex items-center justify-center mb-6 text-blue-400 group-hover:scale-110 transition-transform">
                                <Mic className="w-8 h-8" />
                            </div>
                            <h3 className="text-2xl font-bold mb-3">Far-Field Voice Array</h3>
                            <p className="text-gray-400 leading-relaxed">
                                6-mic array with noise cancellation detects commands from across the room. Crystal clear 2-way communication for calls and voice assistants.
                            </p>
                        </div>

                        {/* Feature 3: RAG */}
                        <div className="p-8 rounded-3xl bg-white/5 border border-white/10 hover:border-green-500/50 transition-all group">
                            <div className="w-14 h-14 rounded-2xl bg-purple-500/20 flex items-center justify-center mb-6 text-purple-400 group-hover:scale-110 transition-transform">
                                <Database className="w-8 h-8" />
                            </div>
                            <h3 className="text-2xl font-bold mb-3">Local RAG System</h3>
                            <p className="text-gray-400 leading-relaxed">
                                Store terabytes of documents securely. The agent "reads" your library and answers questions without ever sending your data to the cloud.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Tech Specs Marquee */}
            <section className="py-20 border-y border-white/5 bg-black/20">
                <div className="container mx-auto px-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
                        <div>
                            <div className="text-4xl font-bold text-white mb-2">16-core</div>
                            <div className="text-green-500 font-medium">Neural Engine</div>
                        </div>
                        <div>
                            <div className="text-4xl font-bold text-white mb-2">153GB/s</div>
                            <div className="text-green-500 font-medium">Memory Bandwidth</div>
                        </div>
                        <div>
                            <div className="text-4xl font-bold text-white mb-2">NVMe</div>
                            <div className="text-green-500 font-medium">Fast Storage</div>
                        </div>
                        <div>
                            <div className="text-4xl font-bold text-white mb-2">Thunderbolt</div>
                            <div className="text-green-500 font-medium">Connectivity</div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section id="waitlist-form" className="py-32 relative overflow-hidden">
                <div className="absolute inset-0 bg-green-900/10" />
                <div className="container mx-auto px-4 text-center relative z-10">
                    <h2 className="text-4xl md:text-6xl font-bold mb-8">Ready to upgrade your home?</h2>
                    <p className="text-xl text-gray-300 mb-4 max-w-2xl mx-auto">
                        Join the waitlist for the LinkBox Agent Hub. Early access members get priority shipping in Q4 2026.
                    </p>

                    {/* Version selector in waitlist */}
                    <div className="flex justify-center gap-4 mb-10">
                        <button
                            onClick={() => setSelectedVersion('standard')}
                            className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all ${selectedVersion === 'standard'
                                ? 'bg-green-500/20 border border-green-500/30 text-green-400'
                                : 'bg-white/5 border border-white/10 text-gray-400 hover:text-white'
                                }`}
                        >
                            Server — $799
                        </button>
                        <button
                            onClick={() => setSelectedVersion('hologram')}
                            className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all ${selectedVersion === 'hologram'
                                ? 'bg-cyan-500/20 border border-cyan-500/30 text-cyan-400'
                                : 'bg-white/5 border border-white/10 text-gray-400 hover:text-white'
                                }`}
                        >
                            Holo — $1,399
                        </button>
                    </div>

                    {isSuccess ? (
                        <div className="max-w-md mx-auto p-6 bg-green-500/10 border border-green-500/20 rounded-xl flex flex-col items-center animate-fade-in-up">
                            <CheckCircle2 className="w-12 h-12 text-green-400 mb-4" />
                            <h3 className="text-2xl font-bold text-white mb-2">You're on the list!</h3>
                            <p className="text-gray-300">We'll notify you when pre-orders open for the {selectedVersion === 'hologram' ? 'Holo' : 'Server'}.</p>
                        </div>
                    ) : (
                        <form onSubmit={handleWaitlistSubmit} className="max-w-md mx-auto flex gap-2">
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                placeholder="Enter your email"
                                className="flex-1 px-6 py-4 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-green-500 transition-colors disabled:opacity-50"
                                disabled={isSubmitting}
                            />
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="px-8 py-4 bg-white text-black font-bold rounded-xl hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                            >
                                {isSubmitting ? (
                                    <>
                                        <span className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                                        Joining...
                                    </>
                                ) : (
                                    'Join Waitlist'
                                )}
                            </button>
                        </form>
                    )}
                </div>
            </section>
            {/* Bottom Spacer */}
            <div className="pb-20" />
        </div>
    )
}
